// services/evaluationService.js - Service for evaluation settings and results
import { apiService } from './apiService';
import { API_CONFIG } from '../config/api';
import { apiUtils } from '../utils/apiUtils';

export const evaluationService = {
  // Get evaluation settings for a box
  getEvaluationSettings: async (boxId) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await apiService.get(API_CONFIG.ENDPOINTS.EVALUATION_SETTINGS(boxId));
      console.log('📊 Evaluation settings retrieved:', response);
      return response;
    } catch (error) {
      console.error(`Error fetching evaluation settings for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getEvaluationSettings');
    }
  },

  // Update evaluation settings for a box
  updateEvaluationSettings: async (boxId, settings) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      if (!settings || typeof settings !== 'object') {
        throw new Error('Valid settings object is required');
      }

      const response = await apiService.put(
        API_CONFIG.ENDPOINTS.EVALUATION_SETTINGS(boxId),
        settings
      );
      console.log('✅ Evaluation settings updated:', response);
      return response;
    } catch (error) {
      console.error(`Error updating evaluation settings for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'updateEvaluationSettings');
    }
  },

  // Get evaluation results for a box
  getEvaluationResults: async (boxId, params = {}) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.EVALUATION_RESULTS(boxId),
        params
      );

      console.log('📊 Evaluation results retrieved:', {
        resultsCount: Array.isArray(response) ? response.length : 1,
        hasData: !!response
      });

      return response;
    } catch (error) {
      console.error(`Error fetching evaluation results for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getEvaluationResults');
    }
  },

  // Get evaluation results with time range
  getEvaluationResultsInRange: async (boxId, startDate, endDate) => {
    try {
      if (!boxId || !startDate || !endDate) {
        throw new Error('Box ID, start date, and end date are required');
      }

      const params = {
        start: startDate instanceof Date ? startDate.toISOString() : startDate,
        end: endDate instanceof Date ? endDate.toISOString() : endDate
      };

      return await evaluationService.getEvaluationResults(boxId, params);
    } catch (error) {
      console.error(`Error fetching evaluation results in range for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getEvaluationResultsInRange');
    }
  },

  // Get latest evaluation result
  getLatestEvaluationResult: async (boxId) => {
    try {
      const results = await evaluationService.getEvaluationResults(boxId);

      if (Array.isArray(results) && results.length > 0) {
        // Return the most recent result (assuming they're ordered by time)
        return results[0];
      } else if (results && !Array.isArray(results)) {
        // Single result returned
        return results;
      }

      return null;
    } catch (error) {
      console.error(`Error fetching latest evaluation result for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getLatestEvaluationResult');
    }
  },

  // List anomaly detectors for a box
  listAnomalyDetectors: async (boxId) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await apiService.get(API_CONFIG.ENDPOINTS.ANOMALY_DETECTORS_LIST(boxId));
      console.log('📋 Anomaly detectors listed:', response);
      return response;
    } catch (error) {
      console.error(`Error listing anomaly detectors for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'listAnomalyDetectors');
    }
  },

  // Get anomaly detector metadata
  getAnomalyDetectorMetadata: async (boxId, version) => {
    try {
      if (!boxId || !version) {
        throw new Error('Box ID and version are required');
      }

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.ANOMALY_DETECTOR_METADATA(boxId, version)
      );
      console.log('📊 Anomaly detector metadata retrieved:', response);
      return response;
    } catch (error) {
      console.error(`Error fetching anomaly detector metadata for box ${boxId}, version ${version}:`, error);
      throw apiUtils.handleApiError(error, 'getAnomalyDetectorMetadata');
    }
  },

  // Download anomaly detector artifact
  downloadAnomalyDetectorArtifact: async (boxId, version) => {
    try {
      if (!boxId || !version) {
        throw new Error('Box ID and version are required');
      }

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.ANOMALY_DETECTOR_ARTIFACT(boxId, version),
        {},
        { responseType: 'blob' } // For downloading files
      );

      console.log('📥 Anomaly detector artifact downloaded');
      return response;
    } catch (error) {
      console.error(`Error downloading anomaly detector artifact for box ${boxId}, version ${version}:`, error);
      throw apiUtils.handleApiError(error, 'downloadAnomalyDetectorArtifact');
    }
  },

  // Upload anomaly detector
  uploadAnomalyDetector: async (boxId, version, file, metadata) => {
    try {
      if (!boxId || !version || !file) {
        throw new Error('Box ID, version, and file are required');
      }

      const formData = new FormData();
      formData.append('file', file);

      if (metadata) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const response = await apiService.post(
        API_CONFIG.ENDPOINTS.ANOMALY_DETECTOR_UPLOAD(boxId, version),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('✅ Anomaly detector uploaded:', response);
      return response;
    } catch (error) {
      console.error(`Error uploading anomaly detector for box ${boxId}, version ${version}:`, error);
      throw apiUtils.handleApiError(error, 'uploadAnomalyDetector');
    }
  },

  // Helper: Parse air quality status
  getAirQualityColor: (status) => {
    const statusMap = {
      'normal': 'success',
      'elevated': 'warning',
      'high': 'error',
      'critical': 'error'
    };
    return statusMap[status?.toLowerCase()] || 'default';
  },

  // Helper: Parse mold risk level
  getMoldRiskColor: (risk) => {
    const riskMap = {
      'low': 'success',
      'moderate': 'warning',
      'high': 'error',
      'critical': 'error'
    };
    return riskMap[risk?.toLowerCase()] || 'default';
  },

  // Helper: Parse trend
  getTrendIcon: (trend) => {
    const trendMap = {
      'stable': '→',
      'rising': '↑',
      'falling': '↓',
      'increasing': '↑',
      'decreasing': '↓'
    };
    return trendMap[trend?.toLowerCase()] || '•';
  }
};